import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  Download,
  Mail,
  Phone,
  Briefcase,
  Users,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES } from "@/data-unevo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "unevo_admin_token";

function LoginGate({ onSuccess }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": token } });
      localStorage.setItem(TOKEN_KEY, token);
      toast.success("Welcome back.");
      onSuccess(token);
    } catch {
      toast.error("Invalid access token.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="grid min-h-screen place-items-center bg-[#0B0F1A] px-6 text-white"
      data-testid="admin-login-gate"
    >
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-10 backdrop-blur">
        <span className="grid h-12 w-12 place-items-center rounded-xl border border-vibrant-orange/40 bg-vibrant-orange/10 text-vibrant-orange">
          <ShieldCheck size={22} />
        </span>
        <h1 className="mt-6 font-heading text-3xl font-extrabold tracking-tight">
          Unevo Admin
        </h1>
        <p className="mt-2 text-sm text-white/60">
          Enter your access token to view leads and applications.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tk" className="text-white/70">Access Token</Label>
            <Input
              id="tk"
              type="password"
              data-testid="admin-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Enter admin token"
              className="border-white/15 bg-white/5 text-white placeholder:text-white/30"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-vibrant-orange px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange-600 disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Verifying..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <div className="flex items-center gap-3 text-emerald-green">
        <Icon size={18} />
        <span className="text-xs font-semibold uppercase tracking-widest">{label}</span>
      </div>
      <p className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white">{value}</p>
    </div>
  );
}

function LeadRow({ lead }) {
  return (
    <tr
      data-testid={`lead-row-${lead.id}`}
      className="border-t border-white/5 transition-colors hover:bg-white/[0.03]"
    >
      <td className="px-4 py-4 text-xs text-white/50">
        {new Date(lead.created_at).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </td>
      <td className="px-4 py-4 font-heading font-semibold text-white">{lead.name}</td>
      <td className="px-4 py-4 text-sm">
        <a href={`mailto:${lead.email}`} className="text-emerald-green hover:underline">
          {lead.email}
        </a>
        {lead.phone && (
          <a
            href={`tel:${lead.phone}`}
            className="ml-3 inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"
          >
            <Phone size={11} /> {lead.phone}
          </a>
        )}
      </td>
      <td className="px-4 py-4 text-sm text-white/70">{lead.company || "—"}</td>
      <td className="px-4 py-4 text-xs">
        {lead.service ? (
          <span className="rounded-full bg-vibrant-orange/15 px-2 py-0.5 font-semibold text-vibrant-orange">
            {lead.service}
          </span>
        ) : (
          <span className="text-white/40">—</span>
        )}
      </td>
      <td className="max-w-xs px-4 py-4 text-xs text-white/60">
        {lead.message ? (
          <span title={lead.message}>
            {lead.message.length > 80 ? lead.message.slice(0, 80) + "…" : lead.message}
          </span>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

export default function AdminLeads() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState("leads");
  const [q, setQ] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async (tk = token) => {
    if (!tk) return;
    setLoading(true);
    try {
      const [l, a] = await Promise.all([
        axios.get(`${API}/leads`, { headers: { "X-Admin-Token": tk } }),
        axios.get(`${API}/applications`, { headers: { "X-Admin-Token": tk } }),
      ]);
      setLeads(l.data);
      setApps(a.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
    setApps([]);
  };

  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      const ql = q.trim().toLowerCase();
      const matchesQ =
        !ql ||
        [l.name, l.email, l.company, l.message]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(ql);
      const matchesService = serviceFilter === "all" || l.service === serviceFilter;
      return matchesQ && matchesService;
    });
  }, [leads, q, serviceFilter]);

  const filteredApps = useMemo(() => {
    return apps.filter((a) => {
      const ql = q.trim().toLowerCase();
      if (!ql) return true;
      return [a.full_name, a.email, a.district, a.message]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(ql);
    });
  }, [apps, q]);

  const downloadCsv = (type) => {
    const url = `${API}/admin/${type}.csv`;
    fetch(url, { headers: { "X-Admin-Token": token } })
      .then((r) => {
        if (!r.ok) throw new Error("bad");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = type === "leads" ? "unevo-leads.csv" : "unnati-applications.csv";
        a.click();
      })
      .catch(() => toast.error("Export failed."));
  };

  if (!token) return (
    <>
      <LoginGate onSuccess={setToken} />
      <Toaster position="top-center" richColors />
    </>
  );

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0B0F1A]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-10">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-xl border border-vibrant-orange/40 bg-vibrant-orange/10 font-heading text-lg font-black text-vibrant-orange">
              U
            </span>
            <div>
              <p className="font-heading text-lg font-extrabold tracking-tight">
                Unevo Admin
              </p>
              <p className="text-[10px] uppercase tracking-widest text-white/40">
                Leads & Applications
              </p>
            </div>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white/70 transition-colors hover:text-white"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">Dashboard</h1>
        <p className="mt-2 text-sm text-white/60">
          Every message, application and hiring request in one place.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Briefcase} label="Total Leads" value={leads.length} />
          <StatCard icon={Users} label="Applications" value={apps.length} />
          <StatCard
            icon={Mail}
            label="This Week"
            value={
              leads.filter(
                (l) => new Date(l.created_at) > new Date(Date.now() - 7 * 24 * 3600 * 1000)
              ).length
            }
          />
        </div>

        {/* Tabs */}
        <div className="mt-10 flex items-center gap-2 border-b border-white/10">
          {["leads", "applications"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`relative px-4 py-3 text-sm font-semibold capitalize transition-colors ${
                tab === t ? "text-white" : "text-white/40 hover:text-white/80"
              }`}
            >
              {t}
              {tab === t && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 bg-vibrant-orange" />
              )}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tab === "leads" ? "Search name, email, company…" : "Search applicant, district…"}
                className="w-full rounded-full border border-white/15 bg-white/5 py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/40 focus:border-vibrant-orange/50 focus:outline-none"
              />
            </div>
            {tab === "leads" && (
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger
                  data-testid="admin-service-filter"
                  className="w-full border-white/15 bg-white/5 text-white sm:w-64"
                >
                  <SelectValue placeholder="Filter by service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All services</SelectItem>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <button
            data-testid="admin-export-btn"
            onClick={() => downloadCsv(tab)}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-vibrant-orange px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-600"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-hidden rounded-3xl border border-white/10">
          {loading ? (
            <div className="grid place-items-center py-16 text-white/50">
              <Loader2 className="animate-spin" size={24} />
            </div>
          ) : tab === "leads" ? (
            filteredLeads.length === 0 ? (
              <div className="grid place-items-center py-16 text-sm text-white/40">
                No leads yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email / Phone</th>
                      <th className="px-4 py-3">Company</th>
                      <th className="px-4 py-3">Service</th>
                      <th className="px-4 py-3">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((l) => (
                      <LeadRow key={l.id} lead={l} />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : filteredApps.length === 0 ? (
            <div className="grid place-items-center py-16 text-sm text-white/40">
              No applications yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email / Phone</th>
                    <th className="px-4 py-3">Module</th>
                    <th className="px-4 py-3">District</th>
                    <th className="px-4 py-3">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((a) => (
                    <tr
                      key={a.id}
                      data-testid={`app-row-${a.id}`}
                      className="border-t border-white/5 transition-colors hover:bg-white/[0.03]"
                    >
                      <td className="px-4 py-4 text-xs text-white/50">
                        {new Date(a.created_at).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-4 font-heading font-semibold text-white">{a.full_name}</td>
                      <td className="px-4 py-4 text-sm">
                        <a href={`mailto:${a.email}`} className="text-emerald-green hover:underline">
                          {a.email}
                        </a>
                        {a.phone && (
                          <span className="ml-3 text-xs text-white/50">{a.phone}</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-xs">
                        <span className="rounded-full bg-emerald-green/15 px-2 py-0.5 font-semibold text-emerald-green">
                          {a.module}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-white/70">{a.district || "—"}</td>
                      <td className="max-w-xs px-4 py-4 text-xs text-white/60">
                        {a.message ? (a.message.length > 80 ? a.message.slice(0, 80) + "…" : a.message) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Toaster position="top-center" richColors />
    </div>
  );
}
